import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Workspaces } from '@air/api';
import { type InviteToken } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getWorkspaceTokensKey } from '~/swr-hooks/members/useInviteTokens';
import { saveTeamInviteNudgeInviteTriggered } from '~/utils/teamInviteNudge';

export const useCreateInviteToken = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const createInviteToken = useCallback(
    async (roleId: string) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      try {
        const newToken = await Workspaces.createInviteToken({ roleId, workspaceId });

        saveTeamInviteNudgeInviteTriggered({ workspaceId });

        queryClient.setQueryData<InviteToken[] | undefined>(getWorkspaceTokensKey(workspaceId), (tokens) => {
          return tokens ? [newToken, ...tokens] : undefined;
        });

        queryClient.invalidateQueries({ queryKey: getWorkspaceTokensKey() });
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Failed to create invite token',
          metadata: {
            Data: {
              roleId,
            },
          },
        });
      }
    },
    [queryClient, workspaceId],
  );

  return {
    createInviteToken,
  };
};
